import React, { useRef, useState } from "react"

import { Form, Formik } from "formik"
import { gql } from "urql"
import { pipe, subscribe } from "wonka"

import { AlertLevel } from "~tailwindui/types/enums"

import { AlertMessageBox } from "~tailwindui/Basics"

import client from "src/urql-client"

import ExternalUserImportStatus from "components/Admin/Organization/ExternalUserImportStatus"
import {
  AutocompleteField,
  CheckboxField,
  FormFailures,
  SubmitButton,
} from "components/Forms/Formik/hookComponents"
import RemoteGradeField from "components/Forms/Formik/hookComponents/RemoteGradeField"
import HeaderTabs from "components/HeaderTabs"

import ClasslinkUsersTable from "./Table"

const OrganizationClasslinkUsers = ({ organizationId }) => {
  const formState = {
    userType: "Student",
    page: 1,
    Student: {
      organizationId,
      importAll: false,
      classlinkUserIds: [],
      filters: {
        classlinkSchoolIds: [],
        gradeNumbers: [],
        classlinkCourseIds: [],
        classlinkClassIds: [],
      },
    },
    Teacher: {
      organizationId,
      importAll: false,
      classlinkUserIds: [],
      filters: {
        classlinkSchoolIds: [],
        classlinkCourseIds: [],
        classlinkClassIds: [],
      },
    },
  }
  const [importComplete, setImportComplete] = useState(false)
  const selectRefs = useRef({})

  const clearSelectFields = () => {
    Object.values(selectRefs.current).forEach(ref => {
      if (!ref) return // the teacher form doesn't have a grade select
      const clearValue = ref.clearValue
        ? ref.clearValue
        : ref.select?.clearValue
      clearValue()
    })
  }

  const handleSubmit = (values, actions) => {
    if (values.userType === "Student") convertStudents(values, actions)
    if (values.userType === "Teacher") convertTeachers(values, actions)
  }

  const convertStudents = (values, actions) => {
    setImportComplete(false)
    const { organizationId, importAll, filters, classlinkUserIds } =
      values[values.userType]
    const params = { organizationId, importAll, classlinkUserIds, ...filters }
    const { unsubscribe } = pipe(
      client.subscription(convertStudentsSubscription, params),
      subscribe(result => {
        if (result.data?.convertClasslinkStudents?.status === "done") {
          actions.setStatus(null)
          setImportComplete(true)
          actions.setSubmitting(false)
          unsubscribe()
        } else if (
          result.data?.convertClasslinkStudents?.errorMessages.length
        ) {
          setImportComplete(false)
          actions.setStatus(
            result.data.convertClasslinkStudents.errorMessages.map(message => ({
              message,
            }))
          )
          actions.setSubmitting(false)
          unsubscribe()
        }
      })
    )
  }

  const convertTeachers = (values, actions) => {
    setImportComplete(false)
    const { organizationId, importAll, filters, classlinkUserIds } =
      values[values.userType]
    const params = { organizationId, importAll, classlinkUserIds, ...filters }
    const { unsubscribe } = pipe(
      client.subscription(convertTeachersSubscription, params),
      subscribe(result => {
        if (result.data?.convertClasslinkTeachers?.status === "done") {
          actions.setStatus(null)
          setImportComplete(true)
          actions.setSubmitting(false)
          unsubscribe()
        } else if (
          result.data?.convertClasslinkTeachers?.errorMessages.length
        ) {
          setImportComplete(false)
          actions.setStatus(
            result.data.convertClasslinkTeachers.errorMessages.map(message => ({
              message,
            }))
          )
          actions.setSubmitting(false)
          unsubscribe()
        }
      })
    )
  }

  return (
    <>
      <ExternalUserImportStatus
        organizationId={organizationId}
        type="classlink"
      />
      <Formik initialValues={formState} onSubmit={handleSubmit}>
        {({ values, setFieldValue, resetForm }) => (
          <Form>
            <HeaderTabs
              selected={values.userType}
              options={[
                {
                  label: "Student",
                  value: "Student",
                  onClick: () => {
                    resetForm()
                    clearSelectFields()
                    setImportComplete(false)
                    setFieldValue("userType", "Student")
                  },
                },
                {
                  label: "Teacher",
                  value: "Teacher",
                  onClick: () => {
                    resetForm()
                    clearSelectFields()
                    setImportComplete(false)
                    setFieldValue("userType", "Teacher")
                  },
                },
              ]}
            />
            <br />
            <div className="flex justify-start">
              <div className="mr-3">
                <h6 className="mt-0">Filter by School</h6>
                <div className="mb-3">
                  <AutocompleteField
                    ref={ref => (selectRefs.current.schools = ref)}
                    name={`${values.userType}.filters.classlinkSchoolIds`}
                    api="/api/admins/classlink_schools/autocomplete_name"
                    searchOptions={`organization_id=${organizationId}`}
                    placeholder="Find School"
                    isMulti
                    isClearable
                    onChange={selectedOpts => {
                      setFieldValue(
                        `${values.userType}.filters.classlinkSchoolIds`,
                        selectedOpts.map(opt => opt.value)
                      )
                      setFieldValue("page", 1)
                    }}
                  />
                </div>
              </div>
              {values.userType === "Student" && (
                <div className="mr-3">
                  <h6 className="mt-0">Filter by Grade</h6>
                  <div className="mb-3">
                    <RemoteGradeField
                      ref={ref => (selectRefs.current.grades = ref)}
                      label=""
                      placeholder="Find Grade"
                      isMulti
                      isClearable
                      onChange={selectedOpts => {
                        setFieldValue(
                          "Student.filters.gradeNumbers",
                          selectedOpts.map(opt => opt.number)
                        )
                        setFieldValue("page", 1)
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="mr-3">
                <h6 className="mt-0">Filter by Course</h6>
                <div className="mb-3">
                  <AutocompleteField
                    ref={ref => (selectRefs.current.courses = ref)}
                    name={`${values.userType}.filters.classlinkCourseIds`}
                    api="/api/admins/classlink_courses/autocomplete_title"
                    searchOptions={`organization_id=${organizationId}`}
                    placeholder="Find Course"
                    isMulti
                    isClearable
                    onChange={selectedOpts => {
                      setFieldValue(
                        `${values.userType}.filters.classlinkCourseIds`,
                        selectedOpts.map(opt => opt.value)
                      )
                      setFieldValue("page", 1)
                    }}
                  />
                </div>
              </div>
              <div className="mr-3">
                <h6 className="mt-0">Filter by Class</h6>
                <div className="mb-3">
                  <AutocompleteField
                    ref={ref => (selectRefs.current.classes = ref)}
                    name={`${values.userType}.filters.classlinkClassIds`}
                    api="/api/admins/classlink_classes/autocomplete_title"
                    searchOptions={`organization_id=${organizationId}`}
                    placeholder="Find Class"
                    isMulti
                    isClearable
                    onChange={selectedOpts => {
                      setFieldValue(
                        `${values.userType}.filters.classlinkClassIds`,
                        selectedOpts.map(opt => opt.value)
                      )
                      setFieldValue("page", 1)
                    }}
                  />
                </div>
              </div>
            </div>
            <ClasslinkUsersTable
              userType={values.userType}
              filters={values[values.userType].filters}
              organizationId={organizationId}
              page={values.page}
              setPage={val => setFieldValue("page", val)}
            />
            <div className="ml-2 mt-5">
              <CheckboxField
                name={`${values.userType}.importAll`}
                label="Import All"
              />
            </div>
            <SubmitButton text={`Import Selected ${values.userType}s`} />
            <FormFailures />
            {importComplete && (
              <AlertMessageBox level={AlertLevel.Info}>
                Import Completed Successfully. Please refresh to see updated
                data.
              </AlertMessageBox>
            )}
          </Form>
        )}
      </Formik>
    </>
  )
}

const convertStudentsSubscription = gql`
  subscription (
    $organizationId: ID!
    $importAll: Boolean!
    $classlinkSchoolIds: [ID!]!
    $classlinkCourseIds: [ID!]!
    $classlinkClassIds: [ID!]!
    $gradeNumbers: [Int!]!
    $classlinkUserIds: [ID!]
  ) {
    convertClasslinkStudents(
      organizationId: $organizationId
      importAll: $importAll
      classlinkSchoolIds: $classlinkSchoolIds
      classlinkCourseIds: $classlinkCourseIds
      classlinkClassIds: $classlinkClassIds
      gradeNumbers: $gradeNumbers
      classlinkStudentIds: $classlinkUserIds
    ) {
      status
      errorMessages
    }
  }
`

const convertTeachersSubscription = gql`
  subscription (
    $organizationId: ID!
    $importAll: Boolean!
    $classlinkSchoolIds: [ID!]!
    $classlinkCourseIds: [ID!]!
    $classlinkClassIds: [ID!]!
    $classlinkUserIds: [ID!]
  ) {
    convertClasslinkTeachers(
      organizationId: $organizationId
      importAll: $importAll
      classlinkSchoolIds: $classlinkSchoolIds
      classlinkCourseIds: $classlinkCourseIds
      classlinkClassIds: $classlinkClassIds
      classlinkTeacherIds: $classlinkUserIds
    ) {
      status
      errorMessages
    }
  }
`

export default OrganizationClasslinkUsers
